/**
 * NaviIntroModal — plays NAVI's illustrated intro video the first time
 * a consumer opens the wallet on this device. On close (skip, X, video
 * end, or Escape) it records the timestamp in wallet storage so the
 * silent avatar takes over for every subsequent visit.
 *
 * The modal can also be re-triggered manually from the About page's
 * "Meet NAVI" tile via the `navi.intro.replay` window event.
 *
 * Video is streamed from the canonical Emergent asset URL so we don't
 * inflate the frontend bundle with a 70 MB MP4.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { X, SkipForward, Volume2, VolumeX } from "lucide-react";
import { loadWallet, updateWallet } from "@/lib/wallet-storage";

const NAVI_INTRO_URL =
  "https://customer-assets-v7afamib.emergentagent.net/job_trust-intelligence-3/artifacts/bncj99o5_Navi%20Intro%20Final.mp4";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

/** Read: has this device seen the intro? */
export function hasSeenNaviIntro() {
  const w = loadWallet() || {};
  return Boolean(w.navi_intro_seen_at);
}

/** Fire from anywhere to replay the intro. */
export function replayNaviIntro() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("navi.intro.replay"));
  }
}

export default function NaviIntroModal({ autoOpenOnFirstVisit = true }) {
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(false);
  const [ended, setEnded] = useState(false);
  const videoRef = useRef(null);

  const markSeen = useCallback(() => {
    updateWallet({ navi_intro_seen_at: new Date().toISOString() });
  }, []);

  const close = useCallback(() => {
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch { /* ignore */ }
    }
    setOpen(false);
    markSeen();
  }, [markSeen]);

  // First-visit auto-open
  useEffect(() => {
    if (!autoOpenOnFirstVisit) return;
    if (!hasSeenNaviIntro()) {
      // Small delay so the wallet home paints first
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [autoOpenOnFirstVisit]);

  // Replay trigger
  useEffect(() => {
    const handler = () => { setEnded(false); setOpen(true); };
    window.addEventListener("navi.intro.replay", handler);
    return () => window.removeEventListener("navi.intro.replay", handler);
  }, []);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!open) return null;

  return (
    <div
      data-testid="navi-intro-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      style={{ background: "rgba(6, 22, 51, 0.94)" }}
      role="dialog"
      aria-modal="true"
      aria-label="Meet NAVI"
    >
      {/* Frame */}
      <div className="relative w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl bg-black">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3" style={{ background: NAVY, color: "white" }}>
          <div className="flex items-center gap-2">
            <span
              className="text-[10px] uppercase tracking-[0.28em] font-semibold"
              style={{ color: GOLD }}
            >
              Meet NAVI
            </span>
            <span className="text-[11px] text-white/70">· A quick hello</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                const next = !muted;
                setMuted(next);
                if (videoRef.current) videoRef.current.muted = next;
              }}
              data-testid="navi-intro-mute"
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              onClick={close}
              data-testid="navi-intro-close"
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Video */}
        <video
          ref={videoRef}
          key={NAVI_INTRO_URL /* forces reload on replay */}
          src={NAVI_INTRO_URL}
          data-testid="navi-intro-video"
          className="w-full block bg-black"
          style={{ aspectRatio: "1 / 1", maxHeight: "70vh" }}
          controls
          autoPlay
          playsInline
          preload="metadata"
          onEnded={() => { setEnded(true); markSeen(); }}
        />

        {/* Footer actions */}
        <div className="flex items-center justify-between gap-2 px-4 py-3" style={{ background: NAVY, color: "white" }}>
          <p className="text-[11.5px] text-white/70 leading-snug pr-2">
            {ended
              ? "That's NAVI. She'll now stay in the corner of every screen — tap her whenever you need something explained."
              : "NAVI is your in-wallet financial explainer. She never gives advice — she just makes sure you understand."}
          </p>
          <button
            onClick={close}
            data-testid="navi-intro-skip"
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold whitespace-nowrap flex-shrink-0"
            style={{ background: GOLD, color: NAVY }}
          >
            {ended ? "Continue" : (<><SkipForward className="w-3.5 h-3.5" /> Skip</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
